import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MatchTable from '../components/MatchTable';
import { apiGet } from '../../services/apiFetch';
import '../../styles/Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalMatches: 0,
    upcomingMatches: 0,
    completedMatches: 0,
    totalTeams: 0,
    totalPlayers: 0
  });
  const [displayStats, setDisplayStats] = useState({
    totalMatches: null,
    upcomingMatches: null,
    completedMatches: null,
    totalTeams: null,
    totalPlayers: null
  });
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [upcomingRes, completedRes, teamsRes, playersRes] = await Promise.all([
          apiGet('/api/rl/dashboard/upcoming'),
          apiGet('/api/rl/dashboard/completed'),
          apiGet('/api/rl/dashboard/teams'),
          apiGet('/api/rl/dashboard/players'),
        ]);
        const upcomingMatches = upcomingRes.data;
        const completedMatches = completedRes.data;
        const teamsData = teamsRes.data;
        const playersData = playersRes.data;

        // Calculate stats
        const statsData = {
          totalMatches: (upcomingMatches?.length || 0) + (completedMatches?.length || 0),
          upcomingMatches: upcomingMatches?.length || 0,
          completedMatches: completedMatches?.length || 0,
          totalTeams: teamsData?.length || 0,
          totalPlayers: playersData?.length || 0
        };

        setStats(statsData);

        // Set recent matches (combining and taking the most recent 5)
        const allMatches = [...(upcomingMatches || []), ...(completedMatches || [])];
        const sortedMatches = allMatches.sort((a, b) => {
          // Sort by date, newest first
          return new Date(b.date) - new Date(a.date);
        });

        // Take the first 5 matches and make sure they have match_status property
        const recentMatchesData = sortedMatches.slice(0, 5).map(match => ({
          ...match,
          match_status: match.match_status || (new Date(match.date) > new Date() ? 'upcoming' : 'completed')
        }));

        setRecentMatches(recentMatchesData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Effect to animate the stats once they're loaded
  useEffect(() => {
    if (!loading && stats) {
      // Start with null values
      setDisplayStats({
        totalMatches: null,
        upcomingMatches: null,
        completedMatches: null,
        totalTeams: null,
        totalPlayers: null
      });

      // Create staggered animations for each stat
      const keys = Object.keys(stats);

      keys.forEach((key, index) => {
        // Stagger the animations
        setTimeout(() => {
          setDisplayStats(prev => ({
            ...prev,
            [key]: 0 // Start count at 0
          }));

          // Count up to the final value
          const finalValue = stats[key];
          const duration = 1000; // 1 second animation
          const startTime = performance.now();

          const animateCount = (timestamp) => {
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out function for more natural animation
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentValue = Math.floor(easeOut * finalValue);

            setDisplayStats(prev => ({
              ...prev,
              [key]: currentValue
            }));

            if (progress < 1) {
              requestAnimationFrame(animateCount);
            } else {
              // Ensure we end with the exact value
              setDisplayStats(prev => ({
                ...prev,
                [key]: finalValue
              }));
            }
          };

          requestAnimationFrame(animateCount);
        }, index * 150); // Stagger each stat by 150ms
      });
    }
  }, [loading, stats]);

  return (
    <div className="dashboard">
      <h1 className="page-title">Dashboard</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <h2>
            {loading ? (
              <span className="stat-placeholder">—</span>
            ) : displayStats.totalMatches === null ? (
              <span className="stat-loading"></span>
            ) : (
              <span className="stat-value">{displayStats.totalMatches}</span>
            )}
          </h2>
          <p>Total Matches</p>
        </div>

        <div className="stat-card">
          <h2>
            {loading ? (
              <span className="stat-placeholder">—</span>
            ) : displayStats.upcomingMatches === null ? (
              <span className="stat-loading"></span>
            ) : (
              <span className="stat-value">{displayStats.upcomingMatches}</span>
            )}
          </h2>
          <p>Upcoming Matches</p>
        </div>

        <div className="stat-card">
          <h2>
            {loading ? (
              <span className="stat-placeholder">—</span>
            ) : displayStats.completedMatches === null ? (
              <span className="stat-loading"></span>
            ) : (
              <span className="stat-value">{displayStats.completedMatches}</span>
            )}
          </h2>
          <p>Completed Matches</p>
        </div>

        <div className="stat-card">
          <h2>
            {loading ? (
              <span className="stat-placeholder">—</span>
            ) : displayStats.totalTeams === null ? (
              <span className="stat-loading"></span>
            ) : (
              <span className="stat-value">{displayStats.totalTeams}</span>
            )}
          </h2>
          <p>Teams</p>
        </div>

        <div className="stat-card">
          <h2>
            {loading ? (
              <span className="stat-placeholder">—</span>
            ) : displayStats.totalPlayers === null ? (
              <span className="stat-loading"></span>
            ) : (
              <span className="stat-value">{displayStats.totalPlayers}</span>
            )}
          </h2>
          <p>Players</p>
        </div>
      </div>

      <div className="dashboard-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link
            to="/admin/matches/create"
            className="action-button"
          >
            Create Match
          </Link>
          <Link
            to="/admin/teams/create"
            className="action-button"
          >
            Create Team
          </Link>
          <Link
            to="/admin/players/create"
            className="action-button"
          >
            Create Player
          </Link>
          <Link
            to="/admin/leagues/create"
            className="action-button"
          >
            Create League
          </Link>
        </div>
      </div>

      <div className="recent-matches">
        <div className="section-header">
          <h2>Recent Matches</h2>
          <Link to="/admin/matches" className="view-all">View All</Link>
        </div>

        {loading ? (
          <div className="loading-spinner"></div>
        ) : recentMatches.length === 0 ? (
          <div className="no-data">No matches found</div>
        ) : (
          <MatchTable filteredMatches={recentMatches} />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
